import { IStyle, mergeStyles } from '@fluentui/react';
import React, { useEffect, useRef, useState } from 'react';
import { internal__getLazyImageFrameStyles } from './styles';

export interface ILazyImageFrameStyles {
    root?: IStyle;
    image?: IStyle;
}

interface ILazyImageFrameProps {
  src: string | undefined;
  alt: string | undefined;
  styles: ILazyImageFrameStyles | undefined;
  onImageLoaded: (src: string) => void;
  onLoadError: (src: string) => void;
}

enum RenderState {
    invisible,
    transistion,
    visible,
}

export const Internal__LazyImageFrame: React.FunctionComponent<ILazyImageFrameProps> = (props) => {

    const img = useRef<HTMLImageElement>(null);
    const renderState = useRef(RenderState.invisible);
    const loadHandledByCallback = useRef(false);
    const [showImage, setShowImage] = useState(false);
    const { src, onImageLoaded } = props;
    const completeTimer = useRef<number | null>(null);

    const styles = internal__getLazyImageFrameStyles();

    const styleRoot = (() => {
        if(showImage) {
            if(renderState.current === RenderState.invisible) {
                renderState.current = RenderState.transistion;
                return styles.transistionRoot;
            } else {
                renderState.current = RenderState.visible;
                return styles.root;
            }
        }

        return styles.hiddenRoot;
    })();
    const rootStyle = !!props.styles?.root ? mergeStyles([styleRoot, props.styles.root])  : mergeStyles([styleRoot]);
    const imageStyle = !!props.styles?.image ? mergeStyles([styles.image, props.styles.image])  : mergeStyles([styles.image]);

    useEffect(() => {
        // wait a bit after src changes, then check complete state on <img/>.
        // We do this because if the image is cached, the load event will not fire
        // In this way, we guarantee that our component's callback will always fire
        if(!!src) {
            completeTimer.current = window.setTimeout(() => {
                if(!loadHandledByCallback.current && img.current?.complete === true) {
                    setShowImage(true);
                    onImageLoaded(src);
                }
                completeTimer.current = null;
                loadHandledByCallback.current = true;
            }, 350);
        }
    }, [src, onImageLoaded]);

    const cancelCompleteTimer = () => {
        if(!completeTimer.current) { return; }

        window.clearTimeout(completeTimer.current);
        completeTimer.current = null;
    };

    const imageLoaded = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
        cancelCompleteTimer();
        setShowImage(true);
        loadHandledByCallback.current = true;

        onImageLoaded(src!);
    };

    const loadError = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
        cancelCompleteTimer();
        showImage && setShowImage(false);
        loadHandledByCallback.current = true;

        props.onLoadError(src!);
    };

  return (
    <>
        <div className={rootStyle}>
            <img
                ref={img}
                className={imageStyle}
                src={props.src}
                alt={props.alt}
                onLoad={imageLoaded}
                onError={loadError}
            />
        </div>
    </>
  );
};
